import { mqMax, mqMin, tokPx } from '@sunwebgroup/ui-components'
import {
  Button,
  Dialog,
  Icon,
  IndeterminateProgressCircle,
  Typography,
} from '@sunwebgroup/ui-components/uds'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { useCMSPageField } from '@/cms/fields'
import { Card } from '@/scopes/common/components/Card'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { OverlayBanner } from '../../common/components'
import { useAuthProtection } from '../../common/hooks'
import bookingsOverviewUseCases from '../domain/useCases'
import { useBookingsOverviewStore } from '../infrastructure/useBookingsOverviewStore'
import {
  AddBooking,
  AddBookingSheet,
  BookingsHistory,
  BookingsOverviewLayout,
  DiscountBanner,
  NoUpcomingBookingCard,
  SelectBookingSheet,
  UpcomingBookingCard,
  Vouchers,
} from './components'
import {
  getSuccessBannerMessageId,
  hasUpcomingBookings,
  isPageError,
  isPageLoading,
  isPageSuccess,
} from './selectors'

const CenterContainer = styled.div`
  margin-top: ${tokPx('primitives_space_80')};
  display: flex;
  justify-content: center;
`

const TopContainer = styled.div`
  display: grid;
  gap: ${tokPx('primitives_space_16')};
  margin-bottom: ${tokPx('primitives_space_48')};

  @media ${mqMin('primitives_breakpoint_l_min')} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const UpcomingBookingCardStyled = styled(UpcomingBookingCard)`
  @media ${mqMin('primitives_breakpoint_m_min')} {
    grid-column: span 2;
  }
`

const AddBookingStyled = styled(AddBooking)`
  @media ${mqMin('primitives_breakpoint_m_min')} {
    grid-column: span 1;
  }
`

const TitleAddBookingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokPx('primitives_space_8')};
  margin-bottom: ${tokPx('primitives_space_4')};
`

const ButtonGroup = styled.div`
  display: flex;
  gap: ${tokPx('primitives_space_8')};

  @media ${mqMax('primitives_breakpoint_s_max')} {
    flex-direction: column;
    width: 100%;

    & > ${Button} {
      width: 100%;
    }
  }
`

const StyledCard = styled(Card)`
  padding: ${tokPx('primitives_space_20')};
  display: flex;
  flex-direction: column;
  grid-column: span 2;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    grid-column: span 1;
  }
`

const NoUpcomingBookingCardStyled = styled(NoUpcomingBookingCard)`
  grid-column: span 2;
`

const VouchersStyled = styled(Vouchers)`
  margin-top: ${tokPx('primitives_space_32')};
`

function BookingsOverviewContent({
  contextItemId,
}: { contextItemId: string }) {
  useAuthProtection()
  const intl = useIntl()
  const { isReady } = useRouter()
  React.useEffect(() => {
    if (isReady)
      bookingsOverviewUseCases.pageLoaded(contextItemId)
  }, [isReady, contextItemId])
  const isLoading = useBookingsOverviewStore(isPageLoading)
  const isError = useBookingsOverviewStore(isPageError)
  const isSuccess = useBookingsOverviewStore(isPageSuccess)
  const successBannerMessageId = useBookingsOverviewStore(getSuccessBannerMessageId)
  const isUpcomingBookingSheetOpen = useBookingsOverviewStore(state => state.isUpcomingBookingSheetOpen)
  const isAddBookingSheetOpen = useBookingsOverviewStore(state => state.isAddBookingSheetOpen)
  const setIn = useBookingsOverviewStore(state => state.setIn)
  const areUpcomingBookingsAvailable = useBookingsOverviewStore(hasUpcomingBookings)
  const discount = useBookingsOverviewStore(state => state.discount)

  const selectBookingButtonRef = React.useRef<HTMLButtonElement>(null)
  const addBookingButtonRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (!isUpcomingBookingSheetOpen && selectBookingButtonRef.current) {
      selectBookingButtonRef.current.focus()
    }
  }, [isUpcomingBookingSheetOpen])

  React.useEffect(() => {
    if (!isAddBookingSheetOpen && addBookingButtonRef.current) {
      addBookingButtonRef.current.focus()
    }
  }, [isAddBookingSheetOpen])

  const openAddBookingSheet = () => {
    setIn('isAddBookingSheetOpen', true)
    setIn('addBookingLoadStatus', 'idle')
  }

  if (isLoading) {
    return (
      <BookingsOverviewLayout>
        <CenterContainer>
          <IndeterminateProgressCircle size={32} />
        </CenterContainer>
      </BookingsOverviewLayout>
    )
  }

  if (isError) {
    return (
      <BookingsOverviewLayout>
        <CenterContainer>
          <Dialog
            type="alert"
            text={intl.formatMessage({ id: 'load_error' })}
            supportingText={intl.formatMessage({ id: 'load_error_supporting_text' })}
          />
        </CenterContainer>
      </BookingsOverviewLayout>
    )
  }

  return (
    <BookingsOverviewLayout
      actions={isSuccess && areUpcomingBookingsAvailable && (
        <ButtonGroup>
          <Button
            ref={selectBookingButtonRef}
            variant="secondary"
            size="M"
            onClick={() => setIn('isUpcomingBookingSheetOpen', true)}
          >
            {intl.formatMessage({ id: 'bookings_overview_select_other_booking' })}
          </Button>
          <Button
            ref={addBookingButtonRef}
            variant="outlinedPrimary"
            size="M"
            onClick={openAddBookingSheet}
          >
            {intl.formatMessage({ id: 'bookings_overview_add_booking_cta' })}
          </Button>
        </ButtonGroup>
      )}
      banner={successBannerMessageId && <OverlayBanner color="success" message={intl.formatMessage({ id: successBannerMessageId })} />}
    >
      {isSuccess && (
        <>
          {discount && <DiscountBanner discountAmount={discount.discountAmount} />}
          <TopContainer>
            {areUpcomingBookingsAvailable && <UpcomingBookingCardStyled />}
            {!areUpcomingBookingsAvailable && (
              <>
                <NoUpcomingBookingCardStyled />
                <StyledCard>
                  <TitleAddBookingContainer>
                    <Icon size={24} glyph="Plus" />
                    <Typography variant="body-l-bold-snug">
                      {intl.formatMessage({ id: 'bookings_overview_add_booking' })}
                    </Typography>
                  </TitleAddBookingContainer>
                  <AddBookingStyled contextItemId={contextItemId} />
                </StyledCard>
              </>
            )}
          </TopContainer>
          <BookingsHistory />
          <VouchersStyled />
        </>
      )}
      <SelectBookingSheet />
      <AddBookingSheet
        isOpen={isAddBookingSheetOpen}
        onOpenChange={open => setIn('isAddBookingSheetOpen', open)}
        contextItemId={contextItemId}
      />
    </BookingsOverviewLayout>
  )
}

const queryClient = new QueryClient()

export function BookingsOverview() {
  const contextItemId = useCMSPageField('contextItemId')

  return (
    <QueryClientProvider client={queryClient}>
      <MetaTitle titleTranslationTag="bookings_overview_meta_title" />
      <BookingsOverviewContent contextItemId={contextItemId} />
    </QueryClientProvider>
  )
}
